import { APIClient } from "../apiCore";
import * as url from "../urls";
import { m9sGlobals } from "../../config";

// The workflow (automation) API of the current cabinet. The screens in
// src/blocks/Workflow keep their own state, so these are plain promises.
const api = new APIClient();

/**
 * The cabinet answers some refusals (plan limits) with HTTP 202 and
 * `success: false`, which axios treats as success. Turn those into rejections
 * so callers have one error path.
 */
const checked = <T = any>(promise: Promise<any>): Promise<T> =>
    promise.then((response: any) => {
        if (response && response.success === false) {
            return Promise.reject(response);
        }

        return response;
    });
const cabinet = (path: string) => m9sGlobals.CURRENT_CABINET + path;

const query = (params: Record<string, any> = {}) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            search.append(key, String(value));
        }
    });
    const text = search.toString();

    return text ? '?' + text : '';
};

// Usage and catalogue
export const getWorkflowUsage = () => checked(api.get(cabinet(url.WORKFLOW_USAGE)));
export const getWorkflowModules = () => checked(api.get(cabinet(url.WORKFLOW_MODULES)));

// Scenarios
export const getWorkflowScenarios = (params: Record<string, any> = {}) => checked(api.get(cabinet(url.WORKFLOW_SCENARIOS + query(params))));
export const getWorkflowScenario = (uuid: string) => checked(api.get(cabinet(url.WORKFLOW_SCENARIO_DETAIL + uuid)));
export const saveWorkflowScenario = (data: any) => checked(api.create(cabinet(url.WORKFLOW_SCENARIO_UPDATE), data));
export const validateWorkflowBlueprint = (blueprint: any, forActivation = false) => checked(api.create(cabinet(url.WORKFLOW_SCENARIO_VALIDATE), { blueprint, forActivation }));
export const removeWorkflowScenario = (uuid: string) => checked(api.create(cabinet(url.WORKFLOW_SCENARIO_REMOVE), { uuid }));
export const importWorkflowScenario = (blueprint: any, name?: string, folder?: string | null) => checked(api.create(cabinet(url.WORKFLOW_SCENARIO_IMPORT), { blueprint, name, folder }));
export const activateWorkflowScenario = (uuid: string) => checked(api.create(cabinet(url.WORKFLOW_SCENARIO + uuid + '/activate'), {}));
export const deactivateWorkflowScenario = (uuid: string) => checked(api.create(cabinet(url.WORKFLOW_SCENARIO + uuid + '/deactivate'), {}));
export const runWorkflowScenario = (uuid: string, payload?: any) => checked(api.create(cabinet(url.WORKFLOW_SCENARIO + uuid + '/run'), payload ? { payload } : {}));
export const exportWorkflowScenario = (uuid: string) => checked(api.get(cabinet(url.WORKFLOW_SCENARIO + uuid + '/export')));
export const getWorkflowScenarioVersions = (uuid: string) => checked(api.get(cabinet(url.WORKFLOW_SCENARIO + uuid + '/versions')));
export const restoreWorkflowScenarioVersion = (uuid: string, version: number) => checked(api.create(cabinet(url.WORKFLOW_SCENARIO + uuid + '/restore/' + version), {}));

// Folders
export const getWorkflowFolders = () => checked(api.get(cabinet(url.WORKFLOW_FOLDERS + query({ maxResults: 200 }))));
export const saveWorkflowFolder = (data: { uuid?: string; name: string }) => checked(api.create(cabinet(url.WORKFLOW_FOLDER_UPDATE), data));
export const removeWorkflowFolder = (uuid: string) => checked(api.create(cabinet(url.WORKFLOW_FOLDER_REMOVE), { uuid }));

// Templates
export const getWorkflowTemplates = (params: Record<string, any> = {}) => checked(api.get(cabinet(url.WORKFLOW_TEMPLATES + query(params))));
export const getWorkflowTemplate = (uuid: string) => checked(api.get(cabinet(url.WORKFLOW_TEMPLATE_DETAIL + uuid)));
export const saveWorkflowTemplate = (data: any) => checked(api.create(cabinet(url.WORKFLOW_TEMPLATE_UPDATE), data));
export const removeWorkflowTemplate = (uuid: string) => checked(api.create(cabinet(url.WORKFLOW_TEMPLATE_REMOVE), { uuid }));
export const applyWorkflowTemplate = (uuid: string, data: { name?: string; folder?: string | null } = {}) => checked(api.create(cabinet(url.WORKFLOW_TEMPLATE + uuid + '/use'), data));

// Executions
export const getWorkflowExecutions = (params: Record<string, any> = {}) => checked(api.get(cabinet(url.WORKFLOW_EXECUTIONS + query(params))));
export const getWorkflowExecution = (uuid: string) => checked(api.get(cabinet(url.WORKFLOW_EXECUTION_DETAIL + uuid)));
export const cancelWorkflowExecution = (uuid: string) => checked(api.create(cabinet(url.WORKFLOW_EXECUTION + uuid + '/cancel'), {}));

// Incoming webhooks
export const getWorkflowHooks = (params: Record<string, any> = {}) => checked(api.get(cabinet(url.WORKFLOW_HOOKS + query({ maxResults: 200, ...params }))));
export const saveWorkflowHook = (data: any) => checked(api.create(cabinet(url.WORKFLOW_HOOK_UPDATE), data));
export const removeWorkflowHook = (uuid: string) => checked(api.create(cabinet(url.WORKFLOW_HOOK_REMOVE), { uuid }));

// User groups (cabinet-wide, used by approvals)
export const getUserGroups = (params: Record<string, any> = {}) => checked(api.get(cabinet(url.USER_GROUPS + query({ maxResults: 200, ...params }))));
export const saveUserGroup = (data: any) => checked(api.create(cabinet(url.USER_GROUP_UPDATE), data));
export const removeUserGroup = (uuid: string) => checked(api.create(cabinet(url.USER_GROUP_REMOVE), { uuid }));

// Data stores
export const getDataStores = () => checked(api.get(cabinet(url.WORKFLOW_DATASTORES + query({ maxResults: 200 }))));
export const saveDataStore = (data: { uuid?: string; name?: string; maxRecords?: number; structure?: { name: string; type: string }[] }) => checked(api.create(cabinet(url.WORKFLOW_DATASTORE_UPDATE), data));
export const removeDataStore = (uuid: string) => checked(api.create(cabinet(url.WORKFLOW_DATASTORE_REMOVE), { uuid }));
export const getDataStoreRecords = (uuid: string, params: Record<string, any> = {}) => checked(api.get(cabinet(url.WORKFLOW_DATASTORE + uuid + '/records' + query(params))));
export const saveDataStoreRecord = (uuid: string, data: { key?: string; data: Record<string, any>; overwrite?: boolean }) => checked(api.create(cabinet(url.WORKFLOW_DATASTORE + uuid + '/record/update'), data));
export const removeDataStoreRecord = (uuid: string, key: string) => checked(api.create(cabinet(url.WORKFLOW_DATASTORE + uuid + '/record/remove'), { key }));

// Approvals (cabinet-wide: channel approval and workflow steps)
export const getApprovals = (params: { scope?: 'mine' | 'requested' | 'all'; status?: string; post?: string } = {}) => checked(api.get(cabinet(url.APPROVALS + query(params))));
export const getApprovalCount = () => checked(api.get(cabinet(url.APPROVAL_COUNT)));
export const getApproval = (uuid: string) => checked(api.get(cabinet(url.APPROVAL_DETAIL + uuid)));
export const decideApproval = (uuid: string, data: { decision: 'approve' | 'reject'; comment?: string; payload?: Record<string, any> }) => checked(api.create(cabinet(url.APPROVAL + uuid + '/decide'), data));
export const getChannelApproval = (channelUuid: string) => checked(api.get(cabinet(url.PEER_CHANNEL_APPROVAL + channelUuid + '/approval')));
export const saveChannelApproval = (channelUuid: string, data: { approvalRequired: boolean; approvalPolicy: any }) => checked(api.create(cabinet(url.PEER_CHANNEL_APPROVAL + channelUuid + '/approval'), data));

/** Full public URL of an incoming webhook (the gateway prefixes the cabinet path). */
export const workflowHookUrl = (path: string) => cabinet(path);
